import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client.safe'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Building2, User, Mail, Phone, ArrowLeft } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface OrganizationRegistrationProps {
  onSuccess: (orgId: string) => void
}

export function OrganizationRegistration({ onSuccess }: OrganizationRegistrationProps) {
  const [formData, setFormData] = useState({
    // Företagsinformation
    companyName: '',
    orgNumber: '',
    billingAddress: '',
    billingEmail: '',
    billingPhone: '',
    contactEmail: '',
    // Administratörsinformation
    adminFullName: '',
    adminPersonnummer: '',
    adminEmail: ''
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const validateForm = (): string | null => {
    // Validate company info
    if (!formData.companyName.trim()) return 'Företagsnamn är obligatoriskt'
    if (!formData.orgNumber.match(/^\d{6}-\d{4}$/)) return 'Organisationsnummer måste vara i formatet XXXXXX-XXXX'
    if (!formData.billingAddress.trim()) return 'Faktureringsadress är obligatorisk'
    if (!formData.billingEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return 'Ogiltig faktureringsemail'
    if (!formData.contactEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return 'Ogiltig kontaktemail'
    
    // Validate admin info
    if (!formData.adminFullName.trim()) return 'Administratörens namn är obligatoriskt'
    if (!formData.adminPersonnummer.match(/^\d{4}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-\d{4}$/)) {
      return 'Personnummer måste vara i formatet ÅÅÅÅMMDD-XXXX'
    }
    if (!formData.adminEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return 'Ogiltig administratörsemail'
    
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationError = validateForm()
    if (validationError) {
      toast({
        title: 'Valideringsfel',
        description: validationError,
        variant: 'destructive'
      })
      return
    }

    setIsSubmitting(true)
    try {
      const { data, error } = await supabase.rpc('register_organization', {
        p_company_name: formData.companyName,
        p_org_number: formData.orgNumber,
        p_billing_address: formData.billingAddress,
        p_billing_email: formData.billingEmail,
        p_billing_phone: formData.billingPhone,
        p_contact_email: formData.contactEmail,
        p_admin_full_name: formData.adminFullName,
        p_admin_personnummer: formData.adminPersonnummer,
        p_admin_email: formData.adminEmail
      })

      if (error) throw error

      toast({
        title: 'Organisation registrerad!',
        description: 'Din revisionsbyrå har skapats och du är nu administratör.'
      })
      
      onSuccess(data)
    } catch (error: any) {
      console.error('Registration error:', error)
      toast({
        title: 'Registreringsfel',
        description: error.message?.includes('organizations_org_number_uniq') ? 
          'Detta organisationsnummer är redan registrerat' :
          error.message?.includes('organizations_billing_email_uniq') ?
          'Denna faktureringsemail används redan' :
          error.message?.includes('profiles_personnummer_uniq') ?
          'Detta personnummer är redan registrerat' :
          'Kunde inte registrera organisation. Försök igen.',
        variant: 'destructive'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/20 p-4">
      <div className="w-full max-w-2xl space-y-6">
        {/* Back to login button */}
        <div className="flex justify-start">
          <Button 
            variant="ghost" 
            onClick={() => window.location.href = '/'}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till inloggning
          </Button>
        </div>
        
        <div className="text-center space-y-2">
          <Building2 className="h-12 w-12 text-primary mx-auto" />
          <h1 className="text-3xl font-bold">Registrera Din Revisionsbyrå</h1>
          <p className="text-muted-foreground">Skapa ett konto för din organisation och börja hantera ärenden</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Företagsinformation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Företagsinformation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="companyName">Företagsnamn *</Label>
                  <Input
                    id="companyName"
                    value={formData.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    placeholder="Revisionsbyrå AB"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="orgNumber">Organisationsnummer *</Label>
                  <Input
                    id="orgNumber"
                    value={formData.orgNumber}
                    onChange={(e) => handleInputChange('orgNumber', e.target.value)}
                    placeholder="XXXXXX-XXXX"
                    pattern="\d{6}-\d{4}"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="billingAddress">Faktureringsadress *</Label>
                <Textarea
                  id="billingAddress"
                  value={formData.billingAddress}
                  onChange={(e) => handleInputChange('billingAddress', e.target.value)}
                  placeholder="Gata 123&#10;123 45 STAD"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="billingEmail">Faktureringsemail *</Label>
                  <Input
                    id="billingEmail"
                    type="email"
                    value={formData.billingEmail}
                    onChange={(e) => handleInputChange('billingEmail', e.target.value)}
                    placeholder="faktura@företag.se"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="billingPhone">Telefonnummer</Label>
                  <Input
                    id="billingPhone"
                    type="tel"
                    value={formData.billingPhone}
                    onChange={(e) => handleInputChange('billingPhone', e.target.value)}
                    placeholder="08-123 456 78"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="contactEmail">Kontaktemail *</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                  placeholder="info@företag.se"
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Administratörsinformation */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Administratörsinformation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="adminFullName">Ditt fullständiga namn *</Label>
                <Input
                  id="adminFullName"
                  value={formData.adminFullName}
                  onChange={(e) => handleInputChange('adminFullName', e.target.value)}
                  placeholder="Anna Andersson"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="adminPersonnummer">Ditt personnummer *</Label>
                  <Input
                    id="adminPersonnummer"
                    value={formData.adminPersonnummer}
                    onChange={(e) => handleInputChange('adminPersonnummer', e.target.value)}
                    placeholder="ÅÅÅÅMMDD-XXXX"
                    pattern="\d{4}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])-\d{4}"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="adminEmail">Din email *</Label>
                  <Input
                    id="adminEmail"
                    type="email"
                    value={formData.adminEmail}
                    onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                    placeholder="anna@företag.se"
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full"
            size="lg"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Registrerar...
              </div>
            ) : (
              'Registrera Organisation'
            )}
          </Button>
        </form>

        <p className="text-sm text-center text-muted-foreground">
          Efter registrering kan du logga in och lägga till teammedlemmar
        </p>
      </div>
    </div>
  )
}